"use client";

import { useCallback, useMemo, useState, type RefObject } from "react";
import { useLocale, useTranslations } from "use-intl";

import { ExpandIcon } from "@/components/appIcons";
import { CountryFlag } from "@/components/country-flag";
import { SearchableSelectSurface } from "@/components/ui/searchable-select-surface";
import { cn } from "@/lib/utils";
import { ALL_COUNTRY_CODES, type CountryCode, formatCountry } from "@garimpa/lib/countries";
import { getPhoneCountryCallingCode } from "@garimpa/lib/phoneNumber";

interface PhoneCountrySelectProps {
  value: CountryCode;
  disabled?: boolean;
  className?: string;
  onChange: (value: CountryCode) => void;
}

export function PhoneCountrySelect({
  value,
  disabled,
  className,
  onChange,
}: PhoneCountrySelectProps) {
  const t = useTranslations("globals.auth");
  const locale = useLocale();
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const countries = useMemo(
    () =>
      ALL_COUNTRY_CODES.flatMap((code) => {
        const callingCode = getPhoneCountryCallingCode(code);
        if (!callingCode) return [];

        return [
          {
            callingCode,
            code,
            name: formatCountry(code, locale, { includeUnknown: true }),
          },
        ];
      }).sort((a, b) => a.name.localeCompare(b.name, locale)),
    [locale],
  );
  const selected = useMemo(
    () => countries.find((country) => country.code === value) ?? countries[0],
    [countries, value],
  );
  const filtered = useMemo(() => {
    const query = inputValue.trim().toLocaleLowerCase(locale);
    if (!query) return countries;

    return countries.filter((country) => {
      const matchesName = country.name.toLocaleLowerCase(locale).includes(query);
      const matchesCountryCode = country.code.toLocaleLowerCase(locale).includes(query);
      const matchesCallingCode = country.callingCode.includes(query.replace(/^\+/, ""));
      return matchesName || matchesCountryCode || matchesCallingCode;
    });
  }, [countries, inputValue, locale]);

  const close = useCallback(() => {
    setIsOpen(false);
    setInputValue("");
    setSelectedIndex(-1);
  }, []);
  const setOpenState = useCallback(
    (open: boolean) => {
      if (open && !disabled) {
        setIsOpen(true);
        return;
      }
      close();
    },
    [close, disabled],
  );
  const selectCountry = useCallback(
    (country: CountryCode) => {
      onChange(country);
      close();
    },
    [close, onChange],
  );
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((index) => (index < filtered.length - 1 ? index + 1 : 0));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((index) => (index > 0 ? index - 1 : filtered.length - 1));
        return;
      }
      if (event.key === "Enter" && selectedIndex >= 0 && selectedIndex < filtered.length) {
        event.preventDefault();
        selectCountry(filtered[selectedIndex].code);
        return;
      }
      if (event.key === "Escape") close();
    },
    [close, filtered, selectedIndex, selectCountry],
  );

  const renderInput = (ref: RefObject<HTMLInputElement | null>, mode: "desktop" | "mobile") => (
    <div className={mode === "mobile" ? "border-b border-border p-2" : "p-2"}>
      <input
        ref={ref}
        type="search"
        value={inputValue}
        placeholder={t("searchPhoneCountry")}
        onChange={(event) => {
          setInputValue(event.currentTarget.value);
          setSelectedIndex(-1);
        }}
        onKeyDown={handleKeyDown}
        className="w-full max-sm:min-h-11 rounded-md border border-border bg-secondary px-2.5 py-2 text-sm outline-none focus:border-ring"
      />
    </div>
  );
  const renderOptions = (ref: RefObject<HTMLUListElement | null>, optionsClassName: string) => (
    <ul ref={ref} className={optionsClassName}>
      {filtered.map((country, index) => {
        const selectedByKeyboard = index === selectedIndex;
        const selectedValue = country.code === value;
        const optionClassName = selectedByKeyboard
          ? "bg-primary text-primary-foreground"
          : selectedValue
            ? "bg-primary/10"
            : "hover:bg-secondary";

        return (
          <li key={country.code}>
            <button
              type="button"
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors",
                optionClassName,
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                selectCountry(country.code);
              }}
            >
              <CountryFlag code={country.code} decorative size="sm" />
              <span className="min-w-0 flex-1 truncate">{country.name}</span>
              <span className="shrink-0 font-medium">+{country.callingCode}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <SearchableSelectSurface
      open={isOpen}
      onOpenChange={setOpenState}
      title={t("phoneCountry")}
      shouldFocus
      selectedIndex={selectedIndex}
      containerClassName="shrink-0"
      desktopPanelClassName="w-72 bg-card"
      desktopOptionsClassName="max-h-60 overflow-y-auto"
      mobilePanelClassName="bg-card"
      renderTrigger={({ setOpen, focusInput }) => (
        <button
          type="button"
          disabled={disabled}
          aria-label={t("phoneCountry")}
          aria-expanded={isOpen}
          className={cn(
            "flex h-11 w-14 items-center justify-center gap-1 rounded-lg sm:h-10 border border-input bg-input/30 px-2 text-sm outline-none transition-all hover:bg-input/50 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          onClick={() => {
            setOpen(true);
            window.setTimeout(focusInput, 0);
          }}
        >
          {selected ? <CountryFlag code={selected.code} decorative size="sm" /> : null}
          <ExpandIcon className="text-base text-muted-foreground" decorative />
        </button>
      )}
      renderInput={renderInput}
      renderOptions={renderOptions}
    />
  );
}
